"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import MyLocationIcon from "@mui/icons-material/MyLocation"
import {
  RRHH_TIPOS_FICHAJE,
  getEmpleadoNombreCompleto,
} from "./rrhhUi.js"

const buildInitialForm = (defaultEmpleado = null, defaultUbicacion = null) => {
  const now = new Date()
  const isoLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

  return {
    idEntidad: defaultEmpleado?.idEntidad || "",
    idUbicacion: defaultUbicacion?.id || defaultEmpleado?.idUbicacionBase || "",
    tipoEvento: "INGRESO",
    fechaHoraEvento: isoLocal,
    observaciones: "",
    latitud: "",
    longitud: "",
    precisionGps: "",
    deviceIdLogico: "web-browser",
  }
}

const ModalRrhhFichaje = ({
  open,
  onClose,
  onSubmit,
  empleadosOptions = [],
  ubicacionesOptions = [],
  submitting = false,
  defaultEmpleado = null,
  defaultUbicacion = null,
}) => {
  const [form, setForm] = useState(buildInitialForm(defaultEmpleado, defaultUbicacion))
  const [error, setError] = useState(null)
  const [geoLoading, setGeoLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(buildInitialForm(defaultEmpleado, defaultUbicacion))
    setError(null)
    setGeoLoading(false)
  }, [open, defaultEmpleado, defaultUbicacion])

  const selectedEmpleado = useMemo(
    () => empleadosOptions.find((option) => option.idEntidad === Number(form.idEntidad)) || null,
    [empleadosOptions, form.idEntidad]
  )

  const selectedUbicacion = useMemo(
    () => ubicacionesOptions.find((option) => option.id === Number(form.idUbicacion)) || null,
    [ubicacionesOptions, form.idUbicacion]
  )

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleCaptureGeolocation = () => {
    if (!navigator.geolocation) {
      setError("Este navegador no soporta geolocalización.")
      return
    }

    setGeoLoading(true)
    setError(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((prev) => ({
          ...prev,
          latitud: String(position.coords.latitude),
          longitud: String(position.coords.longitude),
          precisionGps: String(Math.round(position.coords.accuracy || 0)),
        }))
        setGeoLoading(false)
      },
      (geoError) => {
        setError(geoError.message || "No se pudo obtener la ubicación actual.")
        setGeoLoading(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      }
    )
  }

  const handleSubmit = async () => {
    if (!form.idEntidad || !form.tipoEvento || !form.fechaHoraEvento) {
      setError("Empleado, tipo de evento y fecha/hora son requeridos.")
      return
    }

    setError(null)
    await onSubmit({
      idEntidad: Number(form.idEntidad),
      idUbicacion: form.idUbicacion ? Number(form.idUbicacion) : null,
      tipoEvento: form.tipoEvento,
      fechaHoraEvento: form.fechaHoraEvento,
      observaciones: form.observaciones,
      latitud: form.latitud || null,
      longitud: form.longitud || null,
      precisionGps: form.precisionGps || null,
      fuente: "WEB",
      metodoValidacion: form.latitud && form.longitud ? "GEOLOCALIZACION" : "SIN_VALIDACION",
      deviceIdLogico: form.deviceIdLogico || "web-browser",
    })
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Registrar fichaje</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Autocomplete
            options={empleadosOptions}
            value={selectedEmpleado}
            onChange={(_, value) =>
              setForm((prev) => ({
                ...prev,
                idEntidad: value?.idEntidad || "",
                idUbicacion: prev.idUbicacion || value?.idUbicacionBase || "",
              }))
            }
            getOptionLabel={(option) => {
              const legajo = option.legajo ? ` · ${option.legajo}` : ""
              return `${getEmpleadoNombreCompleto(option)}${legajo}`
            }}
            renderInput={(params) => <TextField {...params} label="Empleado" required />}
          />

          <Autocomplete
            options={RRHH_TIPOS_FICHAJE}
            value={RRHH_TIPOS_FICHAJE.find((option) => option.value === form.tipoEvento) || null}
            onChange={(_, value) => setForm((prev) => ({ ...prev, tipoEvento: value?.value || "" }))}
            renderInput={(params) => <TextField {...params} label="Tipo de evento" required />}
          />

          <Autocomplete
            options={ubicacionesOptions}
            value={selectedUbicacion}
            onChange={(_, value) => setForm((prev) => ({ ...prev, idUbicacion: value?.id || "" }))}
            getOptionLabel={(option) => option.descripcion || String(option.id)}
            renderInput={(params) => <TextField {...params} label="Ubicación" />}
          />

          <TextField
            label="Fecha y hora del evento"
            type="datetime-local"
            value={form.fechaHoraEvento}
            onChange={handleChange("fechaHoraEvento")}
            InputLabelProps={{ shrink: true }}
            required
          />

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              backgroundColor: "rgba(248, 250, 252, 0.9)",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1f2937" }}>
                  Geolocalización opcional
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Si el navegador lo permite, se guarda latitud, longitud y precisión para el fichaje.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={handleCaptureGeolocation}
                disabled={geoLoading}
              >
                {geoLoading ? "Ubicando..." : "Capturar ubicación"}
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
              <TextField label="Latitud" value={form.latitud} onChange={handleChange("latitud")} fullWidth />
              <TextField label="Longitud" value={form.longitud} onChange={handleChange("longitud")} fullWidth />
              <TextField label="Precisión (m)" value={form.precisionGps} onChange={handleChange("precisionGps")} fullWidth />
            </Stack>
          </Box>

          <TextField
            label="Dispositivo lógico"
            value={form.deviceIdLogico}
            onChange={handleChange("deviceIdLogico")}
            placeholder="web-browser"
          />

          <TextField
            label="Observaciones"
            value={form.observaciones}
            onChange={handleChange("observaciones")}
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? "Guardando..." : "Registrar fichaje"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalRrhhFichaje
